import React, { useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MaatwerkQuestionnaire = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.between("mobile", "ipad"));
  const { referenceId } = useParams();
  const location = useLocation();
  const tisref = new URLSearchParams(location.search).get("tisref");

  const [rating, setRating] = useState("");
  const [mainReason, setMainReason] = useState("");
  const [goalAchieved, setGoalAchieved] = useState("");
  const [improveArea, setImproveArea] = useState("");
  const [improveAreaOther, setImproveAreaOther] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const [submitFeedbackLoading, setSubmitFeedbackLoading] = useState(false);

  const handleSubmit = () => {
    if (!rating) return;

    setSubmitFeedbackLoading(true);

    fetch(`${REACT_APP_BACKEND_URL}/questionnaire/${referenceId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        rating,
        main_reason: mainReason,
        goal_achieved: goalAchieved,
        improve_area: improveArea,
        ...(improveArea === "other" && { improve_area_details: improveAreaOther }),
        ...(tisref && { tisref }),
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");
        return res.json();
      })
      .then(() => {
        setSubmitFeedbackLoading(false);
        setSubmitted(true);
      })
      .catch(() => {
        setSubmitFeedbackLoading(false);
        setError(true);
      });

  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        background: "#fff",
        boxShadow: "0 0px 5px rgba(0, 0, 0, 0.1)",
        marginBottom: "32px",
        padding: "24px",
      }}
    >
      <Typography
        variant="h3"
        gutterBottom
        sx={{
          fontSize: "32px",
          color: "#439BD9",
          fontWeight: 600,
          mb: 0,
          ml: 2,
        }}
      >
        {t("maatwerkQuestionnaire.title")}
      </Typography>

      <Divider />

      <Box
        sx={{
          padding: "12px 24px",
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            lineHeight: "180%",
            color: "#454545",
            fontWeight: 400,
          }}
        >
          {t("maatwerkQuestionnaire.subtitle")}
        </Typography>
      </Box>

      {submitted ? (
        <Box sx={{ padding: "0 24px 12px" }}>
          <Alert severity="success">
            {t("maatwerkQuestionnaire.submitSuccess")}
          </Alert>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "32px",
            padding: "0 24px 12px",
          }}
        >
          {/* Vraag 1 - Rating */}
          <FormControl component="fieldset">
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#454545",
                mb: 1,
              }}
            >
              {t("maatwerkQuestionnaire.question1Label")}
            </Typography>
            <RadioGroup
              row
              value={rating}
              onChange={(e) => setRating(e.target.value)}
              sx={{ flexWrap: mobile ? "wrap" : "nowrap", gap: "4px" }}
            >
              {Array.from({ length: 10 }, (_, i) => i + 1).map((val) => (
                <FormControlLabel
                  key={val}
                  value={String(val)}
                  control={
                    <Radio
                      sx={{
                        color: "#439BD9",
                        "&.Mui-checked": { color: "#439BD9" },
                        padding: "4px",
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "14px", color: "#454545" }}>
                      {val}
                    </Typography>
                  }
                  labelPlacement="bottom"
                  sx={{ margin: "0 4px" }}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {/* Vraag 2 - Hoofdreden voor score */}
          <Box>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#454545",
                mb: 1,
              }}
            >
              {t("maatwerkQuestionnaire.question2Label")}
            </Typography>
            <TextField
              multiline
              minRows={3}
              fullWidth
              placeholder={t("maatwerkQuestionnaire.question2Placeholder")}
              value={mainReason}
              onChange={(e) => setMainReason(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": { borderColor: "#439BD9" },
                },
              }}
            />
          </Box>

          {/* Vraag 3 - Doel bereikt */}
          <FormControl component="fieldset">
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#454545",
                mb: 1,
              }}
            >
              {t("maatwerkQuestionnaire.question3Label")}
            </Typography>
            <RadioGroup
              value={goalAchieved}
              onChange={(e) => setGoalAchieved(e.target.value)}
            >
              {["yes_completely", "partly", "no"].map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={
                    <Radio
                      sx={{
                        color: "#439BD9",
                        "&.Mui-checked": { color: "#439BD9" },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "14px", color: "#454545" }}>
                      {t(`maatwerkQuestionnaire.question3Options.${option}`)}
                    </Typography>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>

          {/* Vraag 4 - Verbeterpunt */}
          <FormControl component="fieldset">
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#454545",
                mb: 1,
              }}
            >
              {t("maatwerkQuestionnaire.question4Label")}
            </Typography>
            <RadioGroup
              value={improveArea}
              onChange={(e) => setImproveArea(e.target.value)}
            >
              {["clarity", "speed", "ease_of_use", "communication", "features", "other"].map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={
                    <Radio
                      sx={{
                        color: "#439BD9",
                        "&.Mui-checked": { color: "#439BD9" },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "14px", color: "#454545" }}>
                      {t(`maatwerkQuestionnaire.question4Options.${option}`)}
                    </Typography>
                  }
                />
              ))}
            </RadioGroup>
            <TextField
              fullWidth
              disabled={improveArea !== "other"}
              placeholder={t("maatwerkQuestionnaire.question4OtherPlaceholder")}
              value={improveAreaOther}
              onChange={(e) => setImproveAreaOther(e.target.value)}
              sx={{
                mt: 1,
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": { borderColor: "#439BD9" },
                  "&.Mui-disabled": { backgroundColor: "#f5f5f5" },
                },
              }}
            />
          </FormControl>

          {error && (
            <Alert severity="error">
              {t("maatwerkQuestionnaire.submitError")}
            </Alert>
          )}

          {/* Verzendknop */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "8px" }}>
            {(rating || goalAchieved || improveArea) && (!rating || !goalAchieved || !improveArea) && (
              <Typography sx={{ fontSize: "14px", color: "#E27C00" }}>
                {t("maatwerkQuestionnaire.incompleteWarning")}
              </Typography>
            )}
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!rating || !goalAchieved || !improveArea || submitFeedbackLoading}
              sx={{
                backgroundColor: "#FF8C00",
                textTransform: "none",
                fontWeight: 600,
                padding: "10px 28px",
                borderRadius: "40px",
              }}
            >
              {t("maatwerkQuestionnaire.submit")}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MaatwerkQuestionnaire;
